"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import type { Photo } from '@/lib/photos'
import { toast } from 'sonner'
import { useSettings } from '@/components/branding-provider'
import { findActivePrintProduct, formatPrintProductLabel, type PrintProduct } from '@/lib/print-catalog'

const CART_TOAST_OPTIONS = { id: 'cart-add', duration: 1800, position: 'top-center' as const }
const CART_REMOVE_TOAST_OPTIONS = { id: 'cart-remove', duration: 1800, position: 'top-center' as const }

export type DigitalCartLine = {
  id: string
  kind: 'digital'
  filename: string
  photo: Photo
  quantity: 1
}

export type PrintCartLine = {
  id: string
  kind: 'print'
  filename: string
  photo: Photo
  productId: string
  quantity: number
}

export type CartLine = DigitalCartLine | PrintCartLine

interface CartContextType {
  cart: CartLine[]
  addToCart: (photo: Photo) => void
  addPrintToCart: (photo: Photo, productId: string, quantity?: number) => void
  addPrintsToCart: (photos: Photo[], productId: string) => void
  updatePrintQuantity: (lineId: string, quantity: number) => void
  removeFromCart: (lineId: string) => void
  isInCart: (photoId: string) => boolean
  isPrintInCart: (photoId: string, productId: string) => boolean
  getPrintProduct: (productId: string) => PrintProduct | null
  clearCart: () => void
  cartCount: number
  cartTotal: number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const settings = useSettings()
  const [cart, setCart] = useState<CartLine[]>([])
  const [isHydrated, setIsHydrated] = useState(false)

  // Hydrate from localStorage after mount (avoids SSR/client mismatch)
  useEffect(() => {
    const saved = localStorage.getItem('photo-cart')
    if (saved) {
      try {
        setCart(migrateStoredCart(JSON.parse(saved)))
      } catch (e) {
        console.error('Failed to parse cart from local storage', e)
      }
    }
    setIsHydrated(true)
  }, [])

  // Persist to localStorage on change (only after initial hydration)
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('photo-cart', JSON.stringify(cart))
    }
  }, [cart, isHydrated])

  const addToCart = useCallback((photo: Photo) => {
    setCart(prev => {
      const line = createDigitalLine(photo)
      if (prev.some(item => item.id === line.id)) {
        toast.info("Item already in cart", CART_TOAST_OPTIONS)
        return prev
      }
      toast.success("Added to cart", CART_TOAST_OPTIONS)
      return [...prev, line]
    })
  }, [])

  const getPrintProduct = useCallback((productId: string) => {
    return findActivePrintProduct(settings.commerce?.prints, productId)
  }, [settings.commerce?.prints])

  useEffect(() => {
    if (!isHydrated) return

    setCart(prev => {
      const next = prev.filter(line => line.kind === 'digital' || getPrintProduct(line.productId))
      if (next.length === prev.length) return prev
      toast.info("Unavailable print items were removed from cart", CART_REMOVE_TOAST_OPTIONS)
      return next
    })
  }, [getPrintProduct, isHydrated])

  const addPrintToCart = useCallback((photo: Photo, productId: string, quantity = 1) => {
    const product = getPrintProduct(productId)
    if (!product) {
      toast.error("Print size is unavailable", CART_TOAST_OPTIONS)
      return
    }

    setCart(prev => addPrintLines(prev, [{ photo, product, quantity }], true))
  }, [getPrintProduct])

  const addPrintsToCart = useCallback((photos: Photo[], productId: string) => {
    const product = getPrintProduct(productId)
    if (!product) {
      toast.error("Print size is unavailable", CART_TOAST_OPTIONS)
      return
    }

    setCart(prev => addPrintLines(
      prev,
      photos.map(photo => ({ photo, product, quantity: 1 })),
      false
    ))
    toast.success(`Added ${photos.length} ${formatPrintProductLabel(product)} prints`, CART_TOAST_OPTIONS)
  }, [getPrintProduct])

  const updatePrintQuantity = useCallback((lineId: string, quantity: number) => {
    setCart(prev => prev.map(line => {
      if (line.id !== lineId || line.kind !== 'print') return line
      const product = getPrintProduct(line.productId)
      const maxQuantity = product?.maxQuantity ?? 1
      return {
        ...line,
        quantity: Math.max(1, Math.min(maxQuantity, Math.round(quantity) || 1)),
      }
    }))
  }, [getPrintProduct])

  const removeFromCart = useCallback((lineId: string) => {
    setCart(prev => prev.filter(line => line.id !== lineId))
    toast.success("Removed from cart", CART_REMOVE_TOAST_OPTIONS)
  }, [])

  const isInCart = useCallback((photoId: string) => {
    return cart.some(line => line.kind === 'digital' && line.photo.id === photoId)
  }, [cart])

  const isPrintInCart = useCallback((photoId: string, productId: string) => {
    return cart.some(line => line.kind === 'print' && line.photo.id === photoId && line.productId === productId)
  }, [cart])

  const clearCart = useCallback(() => {
    setCart([])
    localStorage.removeItem('photo-cart')
  }, [])

  const cartTotal = cart.reduce((total, line) => {
    if (line.kind === 'digital') {
      return total + (settings.commerce?.highResPriceCents ?? 1000)
    }
    const product = getPrintProduct(line.productId)
    return total + ((product?.priceCents ?? 0) * line.quantity)
  }, 0)
  const cartCount = cart.reduce((total, line) => total + line.quantity, 0)

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      addPrintToCart,
      addPrintsToCart,
      updatePrintQuantity,
      removeFromCart,
      isInCart,
      isPrintInCart,
      getPrintProduct,
      clearCart,
      cartCount,
      cartTotal
    }}>
      {children}
    </CartContext.Provider>
  )
}

function createDigitalLine(photo: Photo): DigitalCartLine {
  return {
    id: `digital:${photo.id}`,
    kind: 'digital',
    filename: photo.filename,
    photo,
    quantity: 1,
  }
}

function createPrintLine(photo: Photo, product: PrintProduct, quantity: number): PrintCartLine {
  return {
    id: `print:${photo.id}:${product.id}`,
    kind: 'print',
    filename: photo.filename,
    photo,
    productId: product.id,
    quantity,
  }
}

function addPrintLines(
  existingCart: CartLine[],
  additions: Array<{ photo: Photo; product: PrintProduct; quantity: number }>,
  showToast: boolean
) {
  let didChange = false
  const nextCart = [...existingCart]

  for (const addition of additions) {
    const line = createPrintLine(addition.photo, addition.product, addition.quantity)
    const existingIndex = nextCart.findIndex(item => item.id === line.id)
    const existing = existingIndex >= 0 ? nextCart[existingIndex] : null
    if (existing?.kind === 'print') {
      const nextQuantity = Math.min(addition.product.maxQuantity, existing.quantity + addition.quantity)
      if (nextQuantity !== existing.quantity) {
        nextCart[existingIndex] = { ...existing, quantity: nextQuantity }
        didChange = true
      }
      continue
    }

    nextCart.push(line)
    didChange = true
  }

  if (showToast) {
    if (didChange) {
      toast.success("Added print to cart", CART_TOAST_OPTIONS)
    } else {
      toast.info("Print already in cart", CART_TOAST_OPTIONS)
    }
  }

  return didChange ? nextCart : existingCart
}

function migrateStoredCart(value: unknown): CartLine[] {
  if (!Array.isArray(value)) return []

  return value
    .map((item): CartLine | null => {
      if (!item || typeof item !== 'object') return null
      const candidate = item as Partial<CartLine> & Partial<Photo> & { photo?: Photo }

      if (candidate.kind === 'digital' && candidate.photo?.id && candidate.photo.filename) {
        return createDigitalLine(candidate.photo)
      }

      if (candidate.kind === 'print' && candidate.photo?.id && candidate.photo.filename && candidate.productId) {
        return {
          id: candidate.id || `print:${candidate.photo.id}:${candidate.productId}`,
          kind: 'print',
          filename: candidate.photo.filename,
          photo: candidate.photo,
          productId: candidate.productId,
          quantity: Math.max(1, Math.round(candidate.quantity || 1)),
        }
      }

      if (candidate.id && candidate.filename) {
        return createDigitalLine(candidate as Photo)
      }

      return null
    })
    .filter((line): line is CartLine => Boolean(line))
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
